/**
 * Quando uma mensagem notifica alguém, pelas preferências do Discord
 * (`user_guild_settings`) mais o horário de silêncio, que é nosso. Fica no
 * contrato porque o cliente decide a notificação na hora (desktop, web) e a
 * API vai usar a mesma regra no push do celular.
 *
 * Função pura: recebe a mensagem e as preferências, devolve se notifica.
 */

/** 0 todas, 1 só menções, 2 nada, 3 herda (só em canal). */
export type NotificationLevel = 0 | 1 | 2 | 3;

export interface MuteConfig {
  /** Fim do silêncio (ISO); nulo = até desfazer. */
  end_time: string | null;
  selected_time_window?: number | null;
}

export interface ChannelNotificationOverride {
  channel_id: string;
  muted: boolean;
  mute_config: MuteConfig | null;
  message_notifications: NotificationLevel;
  collapsed?: boolean;
}

export interface GuildNotificationSettings {
  /** Nulo: DMs e grupos. */
  guild_id: string | null;
  muted: boolean;
  mute_config: MuteConfig | null;
  /** 3 (ou nulo): o padrão do servidor. */
  message_notifications: NotificationLevel | null;
  suppress_everyone: boolean;
  suppress_roles: boolean;
  mobile_push: boolean;
  channel_overrides: ChannelNotificationOverride[];
}

/** Horário de silêncio (nosso): nada notifica nesse intervalo. */
export interface QuietHours {
  enabled: boolean;
  /** `HH:MM`, no fuso `time_zone`. Fim antes do início: passa da meia-noite. */
  start: string;
  end: string;
  /** Dias (0 domingo … 6 sábado) em que vale; vazio ou ausente = todos. */
  days?: number[];
  time_zone: string;
}

export interface NotificationMessage {
  authorId: string;
  guildId: string | null;
  channelId: string;
  /** Pais do canal, do mais próximo: categoria; numa thread, o canal e a categoria dele. */
  parentIds?: string[];
  /** Menção direta (ou resposta que menciona). */
  mentionsUser: boolean;
  /** Um cargo da pessoa foi mencionado. */
  mentionsRole: boolean;
  /** @everyone ou @here que alcança a pessoa. */
  mentionsEveryone: boolean;
  /** Enviada com @silent (`SUPPRESS_NOTIFICATIONS`). */
  silent?: boolean;
}

export interface NotificationContext {
  userId: string;
  status?: 'online' | 'idle' | 'dnd' | 'invisible' | 'offline';
  quietHours?: QuietHours | null;
  /** `default_message_notifications` do servidor (0 todas, 1 só menções). */
  guildDefault?: 0 | 1;
  settings?: GuildNotificationSettings | null;
  now?: Date;
}

export type NotificationReason =
  | 'own'
  | 'silent'
  | 'dnd'
  | 'quiet-hours'
  | 'muted'
  | 'nothing'
  | 'not-mentioned'
  | 'mention'
  | 'all'
  | 'direct';

export interface NotificationDecision {
  notify: boolean;
  /** Também no celular (`mobile_push`). */
  push: boolean;
  reason: NotificationReason;
}

/** Silêncio ligado e ainda valendo. */
export function isMuteActive(
  muted: boolean,
  config: MuteConfig | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!muted) return false;
  if (!config?.end_time) return true;
  return new Date(config.end_time).getTime() > now.getTime();
}

function minutes(value: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const hours = Number(match[1]);
  const mins = Number(match[2]);
  if (hours > 23 || mins > 59) return null;
  return hours * 60 + mins;
}

/** Dentro do horário de silêncio agora (no fuso escolhido). */
export function inQuietHours(
  quiet: QuietHours | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!quiet?.enabled) return false;
  const start = minutes(quiet.start);
  const end = minutes(quiet.end);
  if (start === null || end === null || start === end) return false;
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone: quiet.time_zone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(now);
  } catch {
    return false;
  }
  const get = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? '';
  const current = Number(get('hour')) * 60 + Number(get('minute'));
  const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(
    get('weekday'),
  );
  let inside: boolean;
  let day = weekday;
  if (start < end) {
    inside = current >= start && current < end;
  } else {
    // Passa da meia-noite: depois da meia-noite conta como o dia anterior.
    inside = current >= start || current < end;
    if (current < end) day = (weekday + 6) % 7;
  }
  if (!inside) return false;
  return !quiet.days?.length || quiet.days.includes(day);
}

/**
 * Notifica? Na ordem: a própria mensagem, @silent, "não perturbe", horário
 * de silêncio; em DM, só o silêncio da conversa; no servidor, o nível do
 * canal (ou da categoria, ou o do servidor, ou o padrão do servidor) e o
 * silêncio (canal, categoria ou servidor), que deixa passar só menções.
 */
export function shouldNotify(
  message: NotificationMessage,
  context: NotificationContext,
): NotificationDecision {
  const now = context.now ?? new Date();
  const settings = context.settings ?? null;
  const push = settings?.mobile_push ?? true;
  const no = (reason: NotificationReason): NotificationDecision => ({
    notify: false,
    push: false,
    reason,
  });
  const yes = (reason: NotificationReason): NotificationDecision => ({
    notify: true,
    push,
    reason,
  });

  if (message.authorId === context.userId) return no('own');
  if (message.silent) return no('silent');
  if (context.status === 'dnd') return no('dnd');
  if (inQuietHours(context.quietHours, now)) return no('quiet-hours');

  const overrides = new Map(
    (settings?.channel_overrides ?? []).map((item) => [item.channel_id, item]),
  );
  const chain = [message.channelId, ...(message.parentIds ?? [])]
    .map((id) => overrides.get(id))
    .filter((item): item is ChannelNotificationOverride => !!item);

  if (!message.guildId) {
    if (chain.some((item) => isMuteActive(item.muted, item.mute_config, now))) {
      return no('muted');
    }
    return yes('direct');
  }

  const muted =
    chain.some((item) => isMuteActive(item.muted, item.mute_config, now)) ||
    isMuteActive(!!settings?.muted, settings?.mute_config, now);
  const fromChannel = chain.find((item) => item.message_notifications !== 3);
  const fromGuild =
    settings?.message_notifications !== undefined &&
    settings?.message_notifications !== null &&
    settings.message_notifications !== 3
      ? settings.message_notifications
      : null;
  const level =
    fromChannel?.message_notifications ??
    fromGuild ??
    context.guildDefault ??
    1;
  if (level === 2) return no('nothing');

  const mentioned =
    message.mentionsUser ||
    (message.mentionsRole && !settings?.suppress_roles) ||
    (message.mentionsEveryone && !settings?.suppress_everyone);
  if (mentioned) return yes('mention');
  if (muted) return no('muted');
  return level === 0 ? yes('all') : no('not-mentioned');
}
